import { difference, differenceBy } from 'lodash';

import type { TiptapContent, TiptapContentNode } from '../tiptap/tiptapTypes';
import { TiptapNodeType } from '../tiptap/tiptapTypes';
import getEditorJsonText from '../tiptap/utils/jsonContentUtils/getEditorJsonText';
import { getTaskItemAssignments } from '../tiptap/extensions/taskList/taskListItemAssignmentUtils';
import { getTaskItemReactions } from '../tiptap/extensions/taskList/taskListItemReactionUtils';
import { getTaskItemLabels } from '../tiptap/extensions/taskList/taskListItemLabelUtils';
import { getTaskItemDueDate } from '../tiptap/extensions/taskList/taskListItemDueDateUtils';
import { isNonEmptyString } from '../utils/stringUtil';
import { type ImMap, prop, propIn } from '../utils/immutableHelper';
import { reduceImTiptapContent } from '../tiptap/utils/jsonContentUtils/reduceImTiptapContent';
import type {
    TaskItemAssignment,
    TaskItemDueDate,
    TaskItemLabel,
    TaskItemReaction,
} from '../tiptap/extensions/taskList/taskListItemChipTypes';

/**
 * The observable state of a single task item, keyed by its stable `taskId`.
 * Used as the input to `diffTaskItemStates` to determine what changed between
 * two snapshots of a task-list document.
 */
export type TaskItemState = {
    text: string;
    assignments: TaskItemAssignment[];
    reactions: TaskItemReaction[];
    labels: TaskItemLabel[];
    dueDate: TaskItemDueDate | null;
    checked: boolean;
};

/**
 * A snapshot of every task item in a document, keyed by `taskId`.
 *
 * A `Map` is used (rather than a plain object) so iteration order is guaranteed to be
 * first-seen document order, which callers can rely on for deterministic diffing/output.
 */
export type TaskItemStateMap = Map<string, TaskItemState>;

/** A due-date/completion/text transition for a single task item between two snapshots. */
export type TaskItemTransition = {
    dueDate: {
        before: TaskItemDueDate | null;
        after: TaskItemDueDate | null;
    };
    checked: {
        before: boolean;
        after: boolean;
    };
    text: {
        before: string;
        after: string;
    };
};

/** The added/removed chip references and transitions for a single task item. */
export type TaskItemDiffEntry = {
    isNew: boolean;
    addedAssigneeIds: string[];
    removedAssigneeIds: string[];
    addedReactions: Array<{ userId: string; emojiCode: string }>;
    removedReactions: Array<{ userId: string; emojiCode: string }>;
    addedLabelIds: string[];
    removedLabelIds: string[];
    transition: TaskItemTransition;
};

/**
 * The per-itemId diff between two `TaskItemStateMap`s, keyed by `taskId`.
 *
 * Only itemIds present in the new map are included (per the plan's spec: "per itemId that
 * exists in the new map"). A `Map` is used for the same first-seen-order reason as
 * `TaskItemStateMap` (iterating the new map's insertion order).
 */
export type TaskItemDiffMap = Map<string, TaskItemDiffEntry>;

/**
 * The result of diffing two `TaskItemStateMap`s: the per-itemId `changed` entries (for
 * itemIds present in the new map) plus `removedItemIds` for itemIds present only in the old
 * map. `removedItemIds` preserves `oldStates`'s first-seen document order.
 */
export type TaskItemStatesDiffResult = {
    changed: TaskItemDiffMap;
    removedItemIds: string[];
};

/**
 * Extracts the text content of a single task item node (its own paragraph/title content,
 * not nested sub-tasks) using the shared `getEditorJsonText` helper.
 *
 * A task item's own content is the leading run of nodes before its nested `taskList`
 * (sub-tasks are nested inside a `taskList` child - see `TaskListItem.tsx`'s schema).
 * `getEditorJsonText` recurses through the *entire* content it's given, so nested
 * sub-task content must be sliced off first or their text would leak into the parent.
 */
const getTaskItemText = (node: TiptapContentNode): string => {
    const ownContent = (node.content ?? []).filter((child) => child.type !== TiptapNodeType.taskList);

    // `getEditorJsonText` expects editor content shaped like a doc, so the task item's
    // own content array is wrapped as a doc's content for the purpose of extraction.
    return getEditorJsonText({ type: TiptapNodeType.doc, content: ownContent });
};

/**
 * The shared per-attr accessors (`getTaskItemAssignments` etc.) only default a
 * missing attr to `[]`/`null` and cast without further validation - fine for their
 * usual editor callers, where the schema guarantees the shape. This util also runs
 * server-side over persisted content that may be malformed, so results are
 * re-guarded with `Array.isArray` here before the per-entry `isNonEmptyString`
 * filtering below.
 */
const asArray = <T>(value: T[]): T[] => (Array.isArray(value) ? value : []);

/** Reads the `dueDate` attr, defaulting to `null` for anything malformed. */
const getValidatedDueDate = (attrs: Record<string, unknown> | undefined): TaskItemDueDate | null => {
    const value = getTaskItemDueDate(attrs);

    if (!value || typeof value !== 'object' || !('timestamp' in value)) return null;

    return value;
};

/**
 * Builds a `TaskItemState` snapshot from a single `taskItem` node's attrs, skipping
 * malformed nested entries rather than throwing.
 */
const buildTaskItemState = (node: TiptapContentNode): TaskItemState => {
    const attrs = node.attrs as Record<string, unknown> | undefined;

    return {
        text: getTaskItemText(node),
        assignments: asArray(getTaskItemAssignments(attrs)).filter((assignment) =>
            isNonEmptyString(assignment?.assigneeId),
        ),
        reactions: asArray(getTaskItemReactions(attrs)).filter(
            (reaction) => isNonEmptyString(reaction?.userId) && isNonEmptyString(reaction?.emojiCode),
        ),
        labels: asArray(getTaskItemLabels(attrs)).filter((label) => isNonEmptyString(label?.labelId)),
        dueDate: getValidatedDueDate(attrs),
        checked: attrs?.checked === true,
    };
};

/**
 * Walks a task-list Tiptap document (recursively, including nested sub-tasks) and returns
 * a snapshot of every task item's observable state, keyed by its `taskId` node attr.
 *
 * Nodes that are not `taskItem`s, or `taskItem`s without a non-empty string `taskId`, are
 * skipped. Iteration order of the returned map is first-seen document order (depth-first).
 */
export const getTaskItemStates = (document: TiptapContent): TaskItemStateMap => {
    const states: TaskItemStateMap = new Map();

    const visitNode = (node: TiptapContentNode): void => {
        if (node.type === TiptapNodeType.taskItem) {
            const taskId = node.attrs?.taskId;

            if (isNonEmptyString(taskId) && !states.has(taskId)) {
                states.set(taskId, buildTaskItemState(node));
            }
        }

        node.content?.forEach(visitNode);
    };

    document.content?.forEach(visitNode);

    return states;
};

/**
 * Walks a task-list document and returns just the set of task ids present, without building each
 * task's observable state - `getTaskItemStates` extracts text and chip arrays that a caller only
 * asking "which tasks exist" would immediately discard.
 *
 * Accepts Immutable or plain content, so a client selector can read an element's stored
 * `textContent` directly rather than deep-converting the whole document first.
 */
export const getTaskItemIds = (document: ImMap<TiptapContentNode> | TiptapContent): Set<string> =>
    reduceImTiptapContent<Set<string>>(
        document as ImMap<TiptapContentNode> | TiptapContentNode,
        (taskIds, node) => {
            if (prop('type', node) !== TiptapNodeType.taskItem) return taskIds;

            const taskId = propIn(['attrs', 'taskId'], node);

            return isNonEmptyString(taskId) ? taskIds.add(taskId) : taskIds;
        },
        new Set<string>(),
    );

const reactionKey = (reaction: { userId: string; emojiCode: string }): string =>
    `${reaction.userId}::${reaction.emojiCode}`;

/**
 * Set difference of reactions (by `userId` + `emojiCode`).
 */
const reactionDiff = (
    before: TaskItemReaction[],
    after: TaskItemReaction[],
): Array<{ userId: string; emojiCode: string }> =>
    differenceBy(after, before, reactionKey).map(({ userId, emojiCode }) => ({ userId, emojiCode }));

/**
 * Compares two task item snapshots and returns the added/removed chip references
 * (by stable identity, not full-object equality) plus due-date/completion transitions.
 */
const diffTaskItemState = (before: TaskItemState | undefined, after: TaskItemState): TaskItemDiffEntry => {
    const beforeAssigneeIds = before?.assignments.map((assignment) => assignment.assigneeId) ?? [];
    const afterAssigneeIds = after.assignments.map((assignment) => assignment.assigneeId);

    const beforeLabelIds = before?.labels.map((label) => label.labelId) ?? [];
    const afterLabelIds = after.labels.map((label) => label.labelId);

    return {
        isNew: before === undefined,
        addedAssigneeIds: difference(afterAssigneeIds, beforeAssigneeIds),
        removedAssigneeIds: difference(beforeAssigneeIds, afterAssigneeIds),
        addedReactions: reactionDiff(before?.reactions ?? [], after.reactions),
        removedReactions: reactionDiff(after.reactions, before?.reactions ?? []),
        addedLabelIds: difference(afterLabelIds, beforeLabelIds),
        removedLabelIds: difference(beforeLabelIds, afterLabelIds),
        transition: {
            dueDate: {
                before: before?.dueDate ?? null,
                after: after.dueDate,
            },
            checked: {
                before: before?.checked ?? false,
                after: after.checked,
            },
            text: {
                before: before?.text ?? '',
                after: after.text,
            },
        },
    };
};

/**
 * Diffs two task item state snapshots (e.g. before/after an edit) and returns `changed` (what
 * changed for every itemId present in `newStates`: added/removed assignees, reactions, and
 * labels by stable identity, plus due-date/completion/text transitions, and whether the item
 * is new) alongside `removedItemIds` for itemIds present only in `oldStates`.
 */
export const diffTaskItemStates = (
    oldStates: TaskItemStateMap,
    newStates: TaskItemStateMap,
): TaskItemStatesDiffResult => {
    const changed: TaskItemDiffMap = new Map();

    newStates.forEach((afterState, taskId) => {
        changed.set(taskId, diffTaskItemState(oldStates.get(taskId), afterState));
    });

    const removedItemIds = Array.from(oldStates.keys()).filter((taskId) => !newStates.has(taskId));

    return { changed, removedItemIds };
};
